import React, { useContext } from "react";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import useUndoneNotification from "../../components/useUndoneNotification";
import taskStateOptions from "@coworker/enums/taskState";
import activityTypes from "@coworker/enums/activityTypes";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import trackerHelper from "../../helpers/tracker";
import { Trans } from "@coworker/locales";
import { useUID } from "../../core/auth/useLoggedInUser";
import { updateQuestionTask } from "../../components/MFAQ/BackendApiRepository/MfaqRepository";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { getRefillAreaIdFromLocation } from "@fixa/store-locations";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import {
  COMPLETED_TASKS_QUERY_KEY,
  IN_PROGRESS_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
} from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";
import { invalidateMediaTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";

export const useUndoCompleteTask = ({ task, afterUndo }) => {
  const undoCurtain = useUndoneNotification();
  const uid = useUID();
  const queryClient = useQueryClient();

  const undoTask = React.useCallback(
    async (actionId) => {
      let data = {
        last_editor_id: uid,
        assigned_user_id: uid,
        completed_note: "",
        state: taskStateOptions.ASSIGNED,
        undo_action_id: actionId || "",
        activity_type: activityTypes.UNDO_COMPLETE,
      };
      if (task.task_type === TaskType.ADDON) {
        data = {
          ...data,
          refilled_quantity: null, // changed to null from "" to make it compatible with tasks service
          refilled_type: "",
          actual_refilled_quantity: null, // changed to null from "" to make it compatible with tasks service
          actual_pick_quantity: task.actual_pick_quantity,
          pick_quantity: task.pick_quantity,
          pick_type: task.pick_type,
          task_type_detail: task.picked ? "REFILL" : "PICK_AND_REFILL",
          searchable_location:
            task.pickup_location_custom ?? task.pickup_location,
        };
      }
      if (task.task_type === TaskType.MFAQ_FOLLOW_UP) {
        data = {
          ...data,
          finished_summary: "",
        };
      }

      await undoCurtain(data, task.id);
      await queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, COMPLETED_TASKS_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
      });
      afterUndo();
    },
    [afterUndo, task, uid, undoCurtain, queryClient]
  );

  return {
    call: undoTask,
  };
};

export const useAcceptPQReport = (task, taskId) => {
  const { showToast } = useToastNotification();
  const uid = useUID();
  const queryClient = useQueryClient();
  const updateTask = useUpdateTaskMutation(false, taskId);
  const { toggleModal } = useContext(ModalContext);

  const acceptPQR = React.useCallback(
    async (completed_note) => {
      let updateData = {
        last_editor_id: uid,
        state: taskStateOptions.COMPLETED,
        action_id: `complete-${new Date().getTime()}`,
        activity_type: activityTypes.COMPLETE,
        completed_note,
        // The onTaskUpdate trigger also deletes undo_action_id, but in the case that this backend
        // change has not propagated to the frontend we must make sure to also signal this from
        // the frontend!
        undo_action_id: "",
      };

      if (task.task_type === TaskType.ADDON) {
        updateData = {
          ...updateData,
          refilled_quantity: task.pick_quantity || task.requested_quantity,
          refilled_type: task.pick_type || task.requested_type,
          pick_quantity: task.pick_quantity || task.requested_quantity,
          actual_refilled_quantity:
            task.actual_pick_quantity || task.actual_requested_quantity,
          actual_pick_quantity:
            task.actual_pick_quantity || task.actual_requested_quantity,
        };
      }

      const result = await updateTask.mutateAsync({ data: updateData, taskId });
      toggleModal({ type: ModalType.TaskDetail });

      const resultId = result?.id;
      if (resultId === taskId) {
        if (updateData.task_type === TaskType.ADDON) {
          trackerHelper.trackRefillAddonTask({ ...task, task_id: taskId });
        }
        trackerHelper.trackCompleteTask(
          { ...task, task_id: taskId },
          { with_changes: false }
        );
        showToast(<Trans>youAcceptedTheTask</Trans>);
        queryClient.invalidateQueries({
          queryKey: [OVERVIEW_TASKS_QUERY_KEY, COMPLETED_TASKS_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
        });
        return true;
      } else {
        showToast(<Trans>searchErrorString</Trans>); // "Oops! Something went wrong." -- most likely only showing up if device is offline but didn't yet switch to the offline view in Fixa.
        return false;
      }
    },
    [showToast, uid, taskId, task, updateTask, queryClient, toggleModal]
  );
  return {
    call: acceptPQR,
    updateTaskMutation: updateTask,
  };
};

export const useCompleteTask = (task, taskId, config = {}) => {
  const {
    undo,
    afterUndo = () => {},
    notificationTranslationKey = "completed2String",
  } = config;
  const uid = useUID();
  const { showToastWithUndo } = useToastNotification();
  const { call: undoTask } = useUndoCompleteTask({
    task: { ...task, id: taskId },
    afterUndo,
  });
  const queryClient = useQueryClient();
  const updateTask = useUpdateTaskMutation(false, taskId);
  const { toggleModal } = useContext(ModalContext);

  const completeTask = React.useCallback(
    async (additionaUpdates = {}) => {
      const actionId = `complete-${new Date().getTime()}`;

      let data = {
        last_editor_id: uid,
        state: taskStateOptions.COMPLETED,
        action_id: actionId,
        activity_type: activityTypes.COMPLETE,
        ...additionaUpdates,
        // The onTaskUpdate trigger also deletes undo_action_id, but in the case that this backend change has not propagated to the frontend we must make sure to also signal this from the frontend!
        undo_action_id: "",
      };

      if (task.task_type === TaskType.ADDON) {
        data = {
          ...data,
          refilled_quantity: task.pick_quantity || task.requested_quantity,
          refilled_type: task.pick_type || task.requested_type,
          pick_quantity: task.pick_quantity || task.requested_quantity,
          actual_refilled_quantity:
            task.actual_pick_quantity || task.actual_requested_quantity,
          actual_pick_quantity:
            task.actual_pick_quantity || task.actual_requested_quantity,
          task_type_detail: "REFILL",
          searchable_location: task.location,
        };
      }

      await updateTask.mutateAsync({ data, taskId });
      toggleModal({ type: ModalType.TaskDetail });

      // Best-effort: sync mfaq-service after task is already committed.
      if (task.task_type === TaskType.MFAQ_FOLLOW_UP) {
        updateQuestionTask(
          task.question_id,
          task.id,
          task.finished_summary,
          data.state
        ).catch(console.error);
      }

      if (task.task_type !== TaskType.MFAQ_FOLLOW_UP) {
        showToastWithUndo(<Trans>{notificationTranslationKey}</Trans>, () =>
          undoTask(actionId)
        );
      }
      trackerHelper.trackCompleteTask(
        { ...task, task_id: taskId },
        { with_changes: false }
      );
      queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, COMPLETED_TASKS_QUERY_KEY],
      });

      if (task.task_type === TaskType.ADDON) {
        trackerHelper.trackRefillAddonTask({ ...task, task_id: taskId });
        queryClient.invalidateQueries({
          queryKey: [
            "articlesByRefillArea",
            getRefillAreaIdFromLocation(
              task.location_grid_code || task.location_custom
            ),
          ],
        });
      }

      if (
        task.task_type === TaskType.ROOM_SETTINGS ||
        task.task_type === "mediaMaintenance"
      ) {
        invalidateMediaTaskQueries(queryClient);
      }

      if (!undo) return;

      return true;
    },
    [
      toggleModal,
      undoTask,
      showToastWithUndo,
      uid,
      taskId,
      task,
      undo,
      notificationTranslationKey,
      queryClient,
      updateTask,
    ]
  );

  return {
    call: completeTask,
    updateTaskMutation: updateTask,
  };
};
